import type { CSSProperties, ReactNode } from "react"
import type { KunciMaskot } from "@/modules/media/domain/slot"

type Mata = "biasa" | "silang" | "senang" | "cemas" | "cari" | "lelah" | "tegas" | "curiga"
type Mulut = "senyum" | "datar" | "sedih" | "o" | "gelombang"

const BODI: CSSProperties = { fill: "hsl(var(--primary))" }
const BODI_GELAP: CSSProperties = { stroke: "hsl(var(--primary-emphasis))" }
const TINTA: CSSProperties = { stroke: "hsl(var(--foreground))" }
const TINTA_ISI: CSSProperties = { fill: "hsl(var(--foreground))" }
const KARTU: CSSProperties = { fill: "hsl(var(--card))" }
const REDUP: CSSProperties = { fill: "hsl(var(--muted-foreground))" }
const BAYANG: CSSProperties = { fill: "hsl(var(--foreground))", opacity: 0.08 }

const AMBER = "#f59e0b"
const KUNING = "#facc15"
const BIRU = "#60a5fa"
const HIJAU = "#16a34a"
const MERAH = "#dc2626"
const LAYAR = "#1b1b20"

function Garis({ d, lebar = 4, gaya = TINTA, isi = "none" }: { d: string; lebar?: number; gaya?: CSSProperties; isi?: string }) {
  return <path d={d} fill={isi} strokeWidth={lebar} strokeLinecap="round" strokeLinejoin="round" style={gaya} />
}

function Lengan({ d }: { d: string }) {
  return <Garis d={d} lebar={10} gaya={BODI_GELAP} />
}

function Mata({ jenis }: { jenis: Mata }) {
  const putih = { fill: "#fff" }
  const garis = { stroke: "#fff", fill: "none", strokeWidth: 3.5, strokeLinecap: "round" as const }
  if (jenis === "silang")
    return (
      <g {...garis}>
        <path d="M97 92l12 12M109 92l-12 12M131 92l12 12M143 92l-12 12" />
      </g>
    )
  if (jenis === "senang")
    return (
      <g {...garis}>
        <path d="M97 102q6-11 12 0M131 102q6-11 12 0" />
      </g>
    )
  if (jenis === "lelah")
    return (
      <g {...garis}>
        <path d="M97 99h13M130 99h13" />
      </g>
    )
  if (jenis === "cari")
    return (
      <g>
        <circle cx="103" cy="97" r="9" {...putih} />
        <circle cx="137" cy="97" r="9" {...putih} />
        <circle cx="107" cy="98" r="4" fill={LAYAR} />
        <circle cx="141" cy="98" r="4" fill={LAYAR} />
      </g>
    )
  if (jenis === "cemas")
    return (
      <g>
        <circle cx="103" cy="99" r="6" {...putih} />
        <circle cx="137" cy="99" r="6" {...putih} />
        <g {...garis} strokeWidth={3}>
          <path d="M95 88l15-5M130 83l15 5" />
        </g>
      </g>
    )
  if (jenis === "tegas")
    return (
      <g>
        <circle cx="103" cy="99" r="6" {...putih} />
        <circle cx="137" cy="99" r="6" {...putih} />
        <g {...garis} strokeWidth={3}>
          <path d="M95 84l15 5M130 89l15-5" />
        </g>
      </g>
    )
  if (jenis === "curiga")
    return (
      <g>
        <circle cx="103" cy="99" r="6" {...putih} />
        <circle cx="137" cy="99" r="3.5" {...putih} />
        <g {...garis} strokeWidth={3}>
          <path d="M95 86l15-3M130 91l15-3" />
        </g>
      </g>
    )
  return (
    <g {...putih}>
      <circle cx="103" cy="98" r="6" />
      <circle cx="137" cy="98" r="6" />
    </g>
  )
}

function Mulut({ jenis }: { jenis: Mulut }) {
  const garis = { stroke: "#fff", fill: "none", strokeWidth: 3.5, strokeLinecap: "round" as const }
  if (jenis === "o") return <circle cx="120" cy="115" r="4.5" {...garis} />
  const d = { senyum: "M109 111q11 9 22 0", datar: "M110 114h20", sedih: "M109 119q11-9 22 0", gelombang: "M106 115q4-5 8 0t8 0t8 0t8 0" }[jenis]
  return <path d={d} {...garis} />
}

interface PropsKarakter {
  mata: Mata
  mulut: Mulut
  lengan?: ReactNode
  atas?: ReactNode
  transform?: string
  antena?: boolean
}

function Karakter({ mata, mulut, lengan, atas, transform, antena = true }: PropsKarakter) {
  return (
    <g transform={transform}>
      {lengan}
      <rect x="86" y="156" width="26" height="14" rx="6" fill={LAYAR} />
      <rect x="128" y="156" width="26" height="14" rx="6" fill={LAYAR} />
      <rect x="66" y="56" width="108" height="108" rx="28" style={BODI} />
      <path d="M78 80q0-14 14-18" fill="none" stroke="#fff" strokeOpacity=".35" strokeWidth="5" strokeLinecap="round" />
      <rect x="80" y="72" width="80" height="54" rx="15" fill={LAYAR} />
      <Mata jenis={mata} />
      <Mulut jenis={mulut} />
      <circle cx="100" cy="146" r="4.5" fill="#fff" />
      <circle cx="120" cy="146" r="4.5" fill="#fff" fillOpacity=".6" />
      <circle cx="140" cy="146" r="4.5" fill="#fff" fillOpacity=".3" />
      {antena && (
        <>
          <path d="M120 56V40" stroke={LAYAR} strokeWidth="5" strokeLinecap="round" />
          <circle cx="120" cy="34" r="7.5" fill={AMBER} />
        </>
      )}
      {atas}
    </g>
  )
}

function Bintang({ x, y, ukuran = 9, warna = AMBER }: { x: number; y: number; ukuran?: number; warna?: string }) {
  const k = ukuran
  return <path d={`M${x} ${y - k}L${x + k * 0.3} ${y - k * 0.3}L${x + k} ${y}L${x + k * 0.3} ${y + k * 0.3}L${x} ${y + k}L${x - k * 0.3} ${y + k * 0.3}L${x - k} ${y}L${x - k * 0.3} ${y - k * 0.3}Z`} fill={warna} />
}

function Tangan({ x, y }: { x: number; y: number }) {
  return <circle cx={x} cy={y} r="8" style={BODI} stroke="hsl(var(--primary-emphasis))" strokeWidth="2" />
}

function Gembok() {
  return (
    <g>
      <Garis d="M30 126v-12a12 12 0 0 1 24 0v12" lebar={6} gaya={{ stroke: "hsl(var(--muted-foreground))" }} />
      <rect x="22" y="124" width="40" height="32" rx="8" fill={AMBER} />
      <circle cx="42" cy="138" r="4" fill={LAYAR} />
      <rect x="40" y="140" width="4" height="9" rx="2" fill={LAYAR} />
    </g>
  )
}

function Adegan({ kunci }: { kunci: KunciMaskot }) {
  switch (kunci) {
    case "sistem-401":
      return (
        <>
          <Gembok />
          <Karakter mata="curiga" mulut="datar" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 108q20-4 20-22" /></>} atas={<Tangan x={194} y={82} />} />
          <circle cx="194" cy="62" r="11" fill="none" stroke={AMBER} strokeWidth="5" />
          <path d="M194 73V80" stroke={AMBER} strokeWidth="5" strokeLinecap="round" />
          <Garis d="M202 62h10" lebar={5} gaya={{ stroke: AMBER }} />
        </>
      )
    case "sistem-403":
      return (
        <>
          <circle cx="44" cy="66" r="20" fill={MERAH} />
          <rect x="32" y="62" width="24" height="8" rx="4" fill="#fff" />
          <Karakter mata="tegas" mulut="datar" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 106q22-4 22-30" /></>} atas={<><Tangan x={196} y={72} /><path d="M192 66l-0 -8M197 64v-9M202 66l3-8" stroke="hsl(var(--primary-emphasis))" strokeWidth="3" strokeLinecap="round" /></>} />
        </>
      )
    case "sistem-404":
      return (
        <>
          <text x="38" y="86" fontSize="40" fontWeight="800" style={REDUP} opacity=".45">?</text>
          <text x="66" y="48" fontSize="22" fontWeight="800" style={REDUP} opacity=".3">?</text>
          <Karakter mata="cari" mulut="o" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 112q16 0 22-8" /></>} atas={<Tangan x={196} y={102} />} />
          <circle cx="204" cy="90" r="17" style={KARTU} fillOpacity=".55" strokeWidth="5" stroke="hsl(var(--foreground))" />
          <Garis d="M216 103l14 15" lebar={7} />
          <path d="M195 86q4-6 11-5" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity=".8" />
        </>
      )
    case "sistem-429":
      return (
        <>
          <path d="M84 62q-6 12 0 18q6-6 0-18z" fill={BIRU} />
          <path d="M160 48q-5 10 0 15q5-5 0-15z" fill={BIRU} />
          <path d="M60 94q-4 8 0 12q4-4 0-12z" fill={BIRU} />
          <Karakter mata="cemas" mulut="gelombang" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 114q14 4 20-6" /></>} atas={<Tangan x={196} y={106} />} />
          <circle cx="206" cy="96" r="20" style={KARTU} strokeWidth="4" stroke="hsl(var(--foreground))" />
          <rect x="201" y="70" width="10" height="6" rx="2" style={TINTA_ISI} />
          <Garis d="M206 96V84M206 96l9 5" lebar={3.5} />
        </>
      )
    case "sistem-500":
      return (
        <>
          <ellipse cx="120" cy="178" rx="84" ry="7" style={BAYANG} />
          <Bintang x={150} y={64} />
          <Bintang x={184} y={42} ukuran={7} warna={KUNING} />
          <Bintang x={120} y={34} ukuran={6} warna={KUNING} />
          <path d="M98 78l8-12 6 8 8-12" fill="none" stroke={AMBER} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="42" cy="104" r="11" style={REDUP} opacity=".3" />
          <circle cx="30" cy="88" r="7" style={REDUP} opacity=".22" />
          <Karakter mata="silang" mulut="gelombang" transform="translate(126 6) rotate(-86 66 164)" />
        </>
      )
    case "sistem-pemeliharaan":
      return (
        <>
          <g transform="translate(46 112)">
            <circle r="17" fill="none" strokeWidth="9" strokeDasharray="7.2 6.9" style={{ stroke: "hsl(var(--muted-foreground))" }} opacity=".5" />
            <circle r="13" fill="none" strokeWidth="6" style={{ stroke: "hsl(var(--muted-foreground))" }} opacity=".5" />
          </g>
          <Karakter
            mata="biasa"
            mulut="senyum"
            antena={false}
            lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 112q20-2 22-22" /></>}
            atas={
              <>
                <path d="M80 60a40 32 0 0 1 80 0z" fill={KUNING} />
                <rect x="74" y="58" width="92" height="8" rx="4" fill={AMBER} />
                <rect x="115" y="36" width="10" height="22" rx="4" fill={AMBER} />
                <Tangan x={196} y={88} />
              </>
            }
          />
          <g transform="rotate(32 196 80)">
            <rect x="192" y="52" width="8" height="52" rx="4" style={TINTA_ISI} opacity=".85" />
            <path d="M184 52a10 10 0 1 1 24 0l-6 0v-8h-12v8z" style={TINTA_ISI} opacity=".85" />
          </g>
        </>
      )
    case "sistem-offline":
      return (
        <>
          <path d="M30 92a15 15 0 0 1 6-28a22 22 0 0 1 42 5a13 13 0 0 1 0 23z" style={REDUP} opacity=".32" />
          <Garis d="M30 54l52 44" lebar={5} gaya={{ stroke: MERAH }} />
          <Karakter mata="lelah" mulut="sedih" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 112q20-2 22-18" /></>} atas={<Tangan x={196} y={92} />} />
          <Garis d="M196 100q6 34-22 40" lebar={4} />
          <rect x="188" y="62" width="16" height="22" rx="4" style={TINTA_ISI} />
          <path d="M193 62v-9M199 62v-9" stroke="hsl(var(--foreground))" strokeWidth="3.5" strokeLinecap="round" />
        </>
      )
    case "konfirmasi-email":
      return (
        <>
          <text x="38" y="84" fontSize="34" fontWeight="800" style={REDUP} opacity=".4">@</text>
          <Bintang x={64} y={40} ukuran={7} />
          <Karakter mata="senang" mulut="senyum" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 112q16 0 22-10" /></>} atas={<Tangan x={196} y={98} />} />
          <rect x="172" y="66" width="52" height="38" rx="6" style={KARTU} stroke="hsl(var(--foreground))" strokeWidth="3.5" />
          <path d="M173 70l25 19l25-19" fill="none" strokeWidth="3.5" strokeLinejoin="round" style={TINTA} />
          <circle cx="222" cy="66" r="7" fill={MERAH} />
        </>
      )
    case "trial-tidak-bisa-diperpanjang":
      return (
        <>
          <Karakter mata="cemas" mulut="datar" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 114q16 0 22-10" /></>} atas={<Tangan x={196} y={100} />} />
          <rect x="178" y="48" width="36" height="6" rx="3" style={TINTA_ISI} />
          <rect x="178" y="106" width="36" height="6" rx="3" style={TINTA_ISI} />
          <path d="M183 54h26l-10 25l10 27h-26l10-27z" style={KARTU} stroke="hsl(var(--foreground))" strokeWidth="3.5" strokeLinejoin="round" />
          <path d="M191 100h10l-5-9z" fill={AMBER} />
          <circle cx="214" cy="56" r="10" fill={MERAH} />
          <path d="M210 52l8 8M218 52l-8 8" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        </>
      )
    case "garansi":
      return (
        <>
          <Bintang x={52} y={70} ukuran={9} />
          <Bintang x={72} y={40} ukuran={6} warna={KUNING} />
          <Bintang x={214} y={44} ukuran={7} warna={KUNING} />
          <Karakter mata="senang" mulut="senyum" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 116q12 2 20-6" /></>} atas={<Tangan x={196} y={106} />} />
          <path d="M196 56h32v28q0 22-16 30q-16-8-16-30z" style={KARTU} stroke="hsl(var(--foreground))" strokeWidth="3.5" strokeLinejoin="round" />
          <path d="M203 86l7 7l12-14" fill="none" stroke={HIJAU} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )
    case "keranjang-kedaluwarsa":
      return (
        <>
          <circle cx="46" cy="62" r="19" style={KARTU} stroke="hsl(var(--foreground))" strokeWidth="3.5" />
          <Garis d="M46 62V50M46 62l9 5" lebar={3.5} />
          <Karakter mata="cemas" mulut="o" lengan={<><Lengan d="M66 108q-16-2-22-22" /><Lengan d="M174 108q16-2 22-22" /></>} atas={<><Tangan x={44} y={84} /><Tangan x={196} y={84} /></>} />
          <Garis d="M180 126h9l7 28h30l7-20h-40" lebar={4.5} />
          <circle cx="204" cy="164" r="5" style={TINTA_ISI} />
          <circle cx="226" cy="164" r="5" style={TINTA_ISI} />
        </>
      )
    case "riwayat-kosong":
      return (
        <>
          <Karakter mata="cari" mulut="datar" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 118q14 6 18 16" /></>} atas={<Tangan x={192} y={136} />} />
          <path d="M168 128l20-12h38l-8 12z" fill="#e2b374" />
          <rect x="168" y="128" width="58" height="42" rx="5" fill="#d29a55" />
          <path d="M168 128l-10-14h20l10 14zM226 128l10-14h-20l-10 14z" fill="#e2b374" />
          <circle cx="44" cy="120" r="5" style={REDUP} opacity=".3" />
          <circle cx="32" cy="104" r="3" style={REDUP} opacity=".25" />
        </>
      )
    case "ulasan-kosong":
      return (
        <>
          <path d="M48 62l4 9l10 1l-8 7l3 10l-9-5l-9 5l3-10l-8-7l10-1z" fill="none" stroke={AMBER} strokeWidth="3" strokeLinejoin="round" strokeDasharray="4 5" />
          <path d="M190 52l3 7l8 1l-6 6l2 8l-7-4l-7 4l2-8l-6-6l8-1z" fill="none" stroke={AMBER} strokeWidth="3" strokeLinejoin="round" strokeDasharray="4 5" />
          <Karakter mata="biasa" mulut="senyum" lengan={<><Lengan d="M66 112q-12 6-12 22" /><Lengan d="M174 112q16 0 20-10" /></>} atas={<><Tangan x={194} y={100} /><path d="M188 92l12-14" stroke={LAYAR} strokeWidth="5" strokeLinecap="round" /><path d="M200 78l5-5" stroke={AMBER} strokeWidth="5" strokeLinecap="round" /></>} />
        </>
      )
    case "transaksi-tidak-ditemukan":
      return (
        <>
          <rect x="22" y="84" width="40" height="52" rx="5" style={KARTU} stroke="hsl(var(--foreground))" strokeWidth="3.5" />
          <path d="M30 98h24M30 108h24M30 118h14" stroke="hsl(var(--muted-foreground))" strokeWidth="3.5" strokeLinecap="round" />
          <text x="164" y="64" fontSize="34" fontWeight="800" style={REDUP} opacity=".45">?</text>
          <Karakter mata="cari" mulut="datar" lengan={<><Lengan d="M66 112q-14 4-14 14" /><Lengan d="M174 112q16 0 22-10" /></>} atas={<Tangan x={196} y={100} />} />
          <circle cx="206" cy="92" r="14" style={KARTU} fillOpacity=".55" strokeWidth="4.5" stroke="hsl(var(--foreground))" />
          <Garis d="M216 102l12 13" lebar={6} />
        </>
      )
    case "pencarian-global":
      return (
        <>
          <circle cx="44" cy="120" r="5" style={REDUP} opacity=".3" />
          <circle cx="32" cy="104" r="3" style={REDUP} opacity=".25" />
          <Karakter mata="cari" mulut="senyum" lengan={<><Lengan d="M66 112q-14 4-14 14" /><Lengan d="M174 112q16 0 22-10" /></>} atas={<Tangan x={196} y={100} />} />
          <circle cx="206" cy="92" r="14" style={KARTU} fillOpacity=".55" strokeWidth="4.5" stroke="hsl(var(--foreground))" />
          <Garis d="M216 102l12 13" lebar={6} />
        </>
      )
  }
}

export function Maskot({ kunci }: { kunci: KunciMaskot }) {
  return (
    <svg viewBox="-6 0 252 190" role="presentation" className="h-full w-full">
      {kunci !== "sistem-500" && <ellipse cx="120" cy="171" rx="56" ry="6" style={BAYANG} />}
      <Adegan kunci={kunci} />
    </svg>
  )
}
